import { useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Background, Controls, ReactFlow, ReactFlowProvider, type Edge } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { ArrowLeft, Ban, ExternalLink, Pencil, RotateCcw } from '@/components/icons';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { formatDateTime, formatDuration, isActive } from '@/lib/format';
import { notifyLabel, toCanvasEdges, toCanvasNodes } from '@/lib/flowView';
import { useIsDark } from '@/lib/useIsDark';
import type { FlowRunDetail } from '@/lib/types';
import { StepNode, type StepNodeType } from '@/components/flows/StepNode';
import { Button, Card, CardHeader, ErrorNotice, LinkButton, PageHeader, PageLoader, StatusPill, useToast } from '@/components/ui';

const nodeTypes = { step: StepNode };

const NODE_STATUS_LABEL: Record<string, string> = {
  PENDING: 'Waiting',
  RUNNING: 'Running',
  PASSED: 'Passed',
  FAILED: 'Failed',
  ERROR: 'Error',
  CANCELLED: 'Cancelled',
  SKIPPED: 'Not run',
};

/** One flow run: the diagram coloured by result (updates while it runs) and every step's report. */
export function FlowRunPage() {
  const { flowRunId = '' } = useParams();
  const run = useQuery({
    queryKey: ['flow-run', flowRunId],
    queryFn: () => api.flowRun(flowRunId),
    refetchInterval: (q) => (q.state.data && isActive(q.state.data.status) ? 2000 : false),
  });
  if (run.isPending) return <PageLoader />;
  if (run.isError) return <ErrorNotice error={run.error} onRetry={() => run.refetch()} />;
  return (
    <ReactFlowProvider>
      <FlowRunView run={run.data} />
    </ReactFlowProvider>
  );
}

function FlowRunView({ run }: { run: FlowRunDetail }) {
  const dark = useIsDark();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const { can } = useAuth();
  const canRun = can('run.trigger', run.project.orgId);
  const active = isActive(run.status);

  const nodes = useMemo(
    () => toCanvasNodes(run.graph, run.names, (n) => ({ run: run.state.nodes[n.id] })) as StepNodeType[],
    [run.graph, run.names, run.state],
  );
  const edges = useMemo(
    () =>
      toCanvasEdges(run.graph).map((e): Edge => {
        const s = run.state.edges[e.id];
        return {
          ...e,
          type: 'smoothstep',
          animated: s === 'taken' && run.state.nodes[e.target]?.status === 'RUNNING',
          style: s === 'skipped' ? { strokeDasharray: '6 6', opacity: 0.45 } : s === 'taken' ? { strokeWidth: 2 } : undefined,
        };
      }),
    [run.graph, run.state],
  );

  const cancel = useMutation({
    mutationFn: () => api.cancelFlowRun(run.id),
    onSuccess: () => {
      toast.success('Flow cancelled - running steps stop after their current test');
      qc.invalidateQueries({ queryKey: ['flow-run', run.id] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const again = useMutation({
    mutationFn: () => api.runFlow(run.flowId!, { collectionId: run.collectionId ?? null }),
    onSuccess: (r) => {
      toast.success('Flow started');
      navigate(`/flow-runs/${r.id}`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const duration = run.startedAt ? (run.finishedAt ? new Date(run.finishedAt).getTime() : Date.now()) - new Date(run.startedAt).getTime() : undefined;
  const stepRows = [...nodes]
    .filter((n) => n.data.node.type !== 'start')
    .sort((a, b) => a.position.y - b.position.y || a.position.x - b.position.x);

  return (
    <div className="space-y-6">
      <Link to={`/projects/${run.projectId}?tab=flows`} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 dark:hover:text-slate-200">
        <ArrowLeft className="size-4" /> {run.project.name}
      </Link>
      <PageHeader
        eyebrow="Flow run"
        title={
          <span className="inline-flex flex-wrap items-center gap-3">
            {run.flowName} <StatusPill status={run.status} />
          </span>
        }
        description={
          <>
            Started {formatDateTime(run.startedAt ?? run.queuedAt)}
            {run.trigger ? ` by ${run.trigger.kind === 'SCHEDULE' ? 'schedule' : 'webhook'} “${run.trigger.name}”` : run.triggeredBy && ` by ${run.triggeredBy.name}`}
            {duration !== undefined && ` · ${formatDuration(duration)}`}
            {active && ' · In progress - the diagram updates automatically.'}
            {run.collectionName && ` · ${run.collectionName} test data`}
            {run.appBuildLabel && ` · build ${run.appBuildLabel}`}
            {run.continueOnFailure && ' · Continues after failures'}
          </>
        }
        actions={
          <div className="flex flex-wrap gap-2">
            {active && canRun && (
              <Button variant="secondary" onClick={() => cancel.mutate()} loading={cancel.isPending} icon={<Ban className="size-4" />}>
                Cancel
              </Button>
            )}
            {!active && canRun && run.flowId && (
              <Button variant="secondary" onClick={() => again.mutate()} loading={again.isPending} icon={<RotateCcw className="size-4" />}>
                Run again
              </Button>
            )}
            {run.flowId && (
              <LinkButton variant="ghost" to={`/projects/${run.projectId}/flows/${run.flowId}`} icon={<Pencil className="size-4" />}>
                Open flow
              </LinkButton>
            )}
          </div>
        }
      />
      {run.error && <ErrorNotice error={new Error(run.error)} />}

      <div className="h-[56vh] min-h-[420px] overflow-hidden rounded-2xl bg-slate-50 ring-1 ring-slate-200 dark:bg-slate-950 dark:ring-slate-800">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          deleteKeyCode={null}
          colorMode={dark ? 'dark' : 'light'}
          fitView
          fitViewOptions={{ padding: 0.3, maxZoom: 1 }}
          proOptions={{ hideAttribution: true }}
          aria-label="Flow run diagram"
        >
          <Background gap={20} />
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>

      <Card>
        <CardHeader title="Steps" description="Every step in reading order. Tests and groups link to their own report." />
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-xs text-slate-500 uppercase">
              <tr className="border-b border-slate-100 dark:border-slate-800">
                <th className="px-5 py-2 font-medium">Step</th>
                <th className="px-5 py-2 font-medium">Result</th>
                <th className="px-5 py-2 font-medium">Details</th>
                <th className="px-5 py-2 font-medium">
                  <span className="sr-only">Report</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {stepRows.map((n) => {
                const st = run.state.nodes[n.id];
                const stats = st?.stats;
                const details =
                  st?.message ??
                  (stats && stats.total > 0 && (n.data.node.type === 'test' || n.data.node.type === 'group') ? `${stats.passed} of ${stats.total} passed` : '');
                return (
                  <tr key={n.id}>
                    <td className="px-5 py-2.5">
                      <div className="font-medium">{n.data.title}</div>
                      <div className="text-xs text-slate-500">{n.data.detail}</div>
                    </td>
                    <td className="px-5 py-2.5">
                      {n.data.node.type === 'condition' && st?.outcome
                        ? `Answer: ${st.outcome === 'yes' ? 'Yes' : 'No'}`
                        : (n.data.node.type === 'notify' ? notifyLabel(st) : undefined) ?? NODE_STATUS_LABEL[st?.status ?? 'PENDING']}
                    </td>
                    <td className="px-5 py-2.5 text-slate-500">{details}</td>
                    <td className="px-5 py-2.5 text-right">
                      {st?.runId && (
                        <Link to={`/runs/${st.runId}`} className="inline-flex items-center gap-1 text-brand-600 hover:underline">
                          Report <ExternalLink className="size-3.5" />
                        </Link>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
